import { KEY_POOL, getCloselyRelatedKeys, getRelativeKey } from '@tunasong/music-lib'
import type { KeySlot } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './cof-key-info-panel.module.css'

export interface CofKeyInfoPanelProps {
  selectedKey: { name: string; ring: 'major' | 'minor' }
  onSelectKey: (k: { name: string; ring: 'major' | 'minor' }) => void
  onPlay: () => void
}

/** Standard order of sharps and flats (the canonical Western convention). */
const SHARPS_ORDER = ['F♯', 'C♯', 'G♯', 'D♯', 'A♯', 'E♯', 'B♯']
const FLATS_ORDER = ['B♭', 'E♭', 'A♭', 'D♭', 'G♭', 'C♭', 'F♭']

const findSlotForKey = (k: { name: string; ring: 'major' | 'minor' }): KeySlot | undefined => {
  if (k.ring === 'major') {
    return KEY_POOL.find(s => s.majorLabels.includes(k.name) || s.canonical === k.name)
  }
  return KEY_POOL.find(s => s.relativeMinor === k.name)
}

const renderSignature = (delta: number): string => {
  if (delta === 0) {
    return 'no sharps or flats'
  }
  if (delta > 0) {
    const accidentals = SHARPS_ORDER.slice(0, delta).join(', ')
    return `${delta} sharp${delta > 1 ? 's' : ''} (${accidentals})`
  }
  const n = -delta
  const accidentals = FLATS_ORDER.slice(0, n).join(', ')
  return `${n} flat${n > 1 ? 's' : ''} (${accidentals})`
}

/**
 * CoF Wheel v1 Explore side-panel. Shows the selected key + signature +
 * relative key + 5-key closely-related cluster + I-V audition replay.
 *
 * Spec: docs/superpowers/specs/2026-05-23-cof-wheel-v1-design.md §9
 */
export const CofKeyInfoPanel: FC<CofKeyInfoPanelProps> = props => {
  const { selectedKey, onSelectKey, onPlay } = props
  const slot = findSlotForKey(selectedKey)
  if (!slot) {
    return null
  }
  const relative = getRelativeKey(selectedKey.name, selectedKey.ring)
  const cluster = getCloselyRelatedKeys(slot.canonical)
  const headerLabel =
    selectedKey.ring === 'major' ? `${selectedKey.name} major` : `${selectedKey.name} minor`
  const relativeLabel =
    relative.ring === 'major' ? `${relative.name} major` : `${relative.name} minor`

  return (
    <aside className={styles.panel} aria-label="Key information">
      <h2 className={styles.heading}>{headerLabel}</h2>
      <p className={styles.signature}>{renderSignature(slot.signatureDelta)}</p>
      <Button variant="outlined" size="small" onClick={onPlay} className={styles.playBtn}>
        Hear it
      </Button>
      <div className={styles.row}>
        <span className={styles.rowLabel}>Relative:</span>
        <Button
          variant="text"
          size="small"
          onClick={() => onSelectKey(relative)}
          className={styles.linkBtn}
        >
          {relativeLabel}
        </Button>
      </div>
      <div className={styles.cluster}>
        <span className={styles.rowLabel}>Closely-related:</span>
        <div className={styles.clusterRow}>
          {cluster.map(name => {
            const ring: 'major' | 'minor' = name.endsWith('m') ? 'minor' : 'major'
            const plain = name.replace(/m$/, '')
            return (
              <Button
                key={name}
                variant="text"
                size="small"
                onClick={() => onSelectKey({ name: plain, ring })}
                className={styles.linkBtn}
              >
                {name}
              </Button>
            )
          })}
        </div>
      </div>
    </aside>
  )
}
